import { z } from 'zod';
import type { PaymentTypeToken } from '@sleed/bizcommerce-api';

/**
 * Payment type (Τρόπος Πληρωμής) edit form. Mirrors `PaymentTypeToken` and the legacy admin popup
 * (Core/client/admin/src/pages/Orders/PaymentTypes/index.js): nm required ≤100, opt (online payment
 * type) required, cd ≤50, erpcd/erpcd2 optional, cid/rid optional FKs, frt/tprc/minprc/maxprc/minw/maxw
 * optional numbers, retail_only/invoice_only/supportStoredCards/al_ed_order booleans, iv visible,
 * si required, installments_logic free text, CustomerGroupIds optional list, description HTML, img1 image.
 * `nm` is multilanguage server-side (writes go to the active Accept-Language, default `el`). Image bytes
 * upload out-of-band (img1File → binaryRepoImagesApi.uploadPaymentType, needs a saved id).
 */
const nullableStr = z.string().nullable();
/** Empty string / null → null; otherwise the parsed number. */
const nullableNumber = z.preprocess(v => (v === '' || v === null || v === undefined ? null : Number(v)), z.number().nullable());

export const paymentTypeSchema = z.object({
	id: z.number().optional(),
	nm: z.string().trim().min(1, 'validation.required').max(100, 'validation.tooLong'),
	cd: z.string().trim().max(50, 'validation.tooLong').nullable(),
	erpcd: nullableStr,
	erpcd2: nullableStr,
	cid: z.number().nullable(),
	cids: z.array(z.number()),
	rid: z.number().nullable(),
	rids: z.array(z.number()),
	opt: z.number({ message: 'validation.required' }).int(),
	frt: nullableNumber,
	tprc: nullableNumber,
	minprc: nullableNumber,
	maxprc: nullableNumber,
	minw: nullableNumber,
	maxw: nullableNumber,
	retail_only: z.boolean(),
	invoice_only: z.boolean(),
	supportStoredCards: z.boolean(),
	al_ed_order: z.boolean(),
	installments_logic: nullableStr,
	iv: z.boolean(),
	si: z.number({ message: 'validation.required' }).int(),
	description: nullableStr,
	CustomerGroupIds: z.array(z.number()).nullable(),
	ExcludedCustomerGroupIds: z.array(z.number()).nullable(),
	img1: nullableStr,
	imgdel1: z.boolean(),
	img1File: z.instanceof(File).nullable()
});

export type PaymentTypeFormValues = z.infer<typeof paymentTypeSchema>;

export const EMPTY_PAYMENT_TYPE: PaymentTypeFormValues = {
	nm: '',
	cd: null,
	erpcd: null,
	erpcd2: null,
	cid: null,
	cids: [],
	rid: null,
	rids: [],
	opt: 0,
	frt: null,
	tprc: null,
	minprc: null,
	maxprc: null,
	minw: null,
	maxw: null,
	retail_only: false,
	invoice_only: false,
	supportStoredCards: false,
	al_ed_order: false,
	installments_logic: null,
	iv: true,
	si: 0,
	description: null,
	CustomerGroupIds: [],
	ExcludedCustomerGroupIds: [],
	img1: null,
	imgdel1: false,
	img1File: null
};

export function tokenToForm(token: PaymentTypeToken): PaymentTypeFormValues {
	return {
		id: token.id,
		nm: token.nm ?? '',
		cd: token.cd ?? null,
		erpcd: token.erpcd ?? null,
		erpcd2: token.erpcd2 ?? null,
		cid: token.cid ?? null,
		// Auto-adopt a legacy single country when the multi-set is empty (parity with the grid + backend read).
		cids: token.cids && token.cids.length > 0 ? token.cids : token.cid != null ? [token.cid] : [],
		rid: token.rid ?? null,
		// Auto-adopt a legacy single region when the multi-set is empty (BW-627, parity with cids).
		rids: token.rids && token.rids.length > 0 ? token.rids : token.rid != null ? [token.rid] : [],
		opt: token.opt ?? 0,
		frt: token.frt ?? null,
		tprc: token.tprc ?? null,
		minprc: token.minprc ?? null,
		maxprc: token.maxprc ?? null,
		minw: token.minw ?? null,
		maxw: token.maxw ?? null,
		retail_only: token.retail_only ?? false,
		invoice_only: token.invoice_only ?? false,
		supportStoredCards: token.supportStoredCards ?? false,
		al_ed_order: token.al_ed_order ?? false,
		installments_logic: token.installments_logic ?? null,
		iv: token.iv ?? false,
		si: token.si ?? 0,
		description: token.description ?? null,
		CustomerGroupIds: token.CustomerGroupIds ?? [],
		ExcludedCustomerGroupIds: token.ExcludedCustomerGroupIds ?? [],
		img1: token.img1 ?? null,
		imgdel1: false,
		img1File: null
	};
}

export function formToToken(form: PaymentTypeFormValues): PaymentTypeToken {
	return {
		id: form.id,
		nm: form.nm.trim(),
		cd: form.cd?.trim() || null,
		erpcd: form.erpcd,
		erpcd2: form.erpcd2,
		// Keep the legacy scalar consistent with the multi-set: null when cleared, so the backend does not
		// resurrect a stale cid (its empty-cids back-compat backfill only kicks in for true legacy callers).
		cid: form.cids.length > 0 ? form.cids[0] : null,
		cids: form.cids,
		// Same for the legacy region scalar (BW-627).
		rid: form.rids.length > 0 ? form.rids[0] : null,
		rids: form.rids,
		opt: form.opt,
		frt: form.frt,
		tprc: form.tprc,
		minprc: form.minprc,
		maxprc: form.maxprc,
		minw: form.minw,
		maxw: form.maxw,
		retail_only: form.retail_only,
		invoice_only: form.invoice_only,
		supportStoredCards: form.supportStoredCards,
		al_ed_order: form.al_ed_order,
		installments_logic: form.installments_logic,
		iv: form.iv,
		si: form.si,
		description: form.description,
		CustomerGroupIds: form.CustomerGroupIds,
		ExcludedCustomerGroupIds: form.ExcludedCustomerGroupIds,
		img1: form.img1,
		imgdel1: form.imgdel1
	};
}
